import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  CaretLeft, CaretRight, Calendar, List as ListIcon, MapTrifold, Plus,
  Path,
} from "@phosphor-icons/react";
import RouteMap from "../../components/routes/RouteMap";

// Weekdays Mon..Sun
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function startOfWeek(d) {
  // Monday-based week start
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  const day = (x.getDay() + 6) % 7;  // 0 = Monday
  x.setDate(x.getDate() - day);
  return x;
}

function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function monthStart(d) { const x = new Date(d); x.setDate(1); x.setHours(0,0,0,0); return x; }
function monthEnd(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

const STATUS_STYLE = {
  NEW: "bg-[#E5E2D9] text-[#5C5A55]",
  QUOTED: "bg-[#FDE7C3] text-[#7A5A11]",
  APPROVED: "bg-[#D4E0D1] text-[#2D5A27]",
  SCHEDULED: "bg-[#BBDEFB] text-[#0D47A1]",
  IN_PROGRESS: "bg-[#F9E4B7] text-[#7A5A11]",
  COMPLETED: "bg-[#2D5A27] text-white",
  CANCELLED: "bg-[#F4C7B9] text-[#7A2E18]",
  MISSED: "bg-[#F4C7B9] text-[#7A2E18]",
};

export default function RouteDailyView() {
  const nav = useNavigate();
  const [selected, setSelected] = useState(ymd(new Date()));
  const [cursor, setCursor] = useState(new Date());   // anchors current month/week
  const [mode, setMode] = useState("week");           // week | month
  const [requests, setRequests] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [view, setView] = useState("list");            // list | map
  const [loading, setLoading] = useState(false);
  const [monthCounts, setMonthCounts] = useState({});  // {YYYY-MM-DD: count}

  const weekStart = useMemo(() => startOfWeek(cursor), [cursor]);
  const weekDays = useMemo(() =>
    Array.from({ length: 7 }).map((_, i) => addDays(weekStart, i)), [weekStart]);

  const mStart = useMemo(() => monthStart(cursor), [cursor]);
  const mEnd = useMemo(() => monthEnd(cursor), [cursor]);
  const monthGrid = useMemo(() => {
    // Mon-start calendar grid: pad leading blanks so mStart lands on its weekday
    const leading = (mStart.getDay() + 6) % 7;
    const days = [];
    for (let i = 0; i < leading; i++) days.push(null);
    for (let d = new Date(mStart); d <= mEnd; d = addDays(d, 1)) days.push(new Date(d));
    while (days.length % 7 !== 0) days.push(null);
    return days;
  }, [mStart, mEnd]);

  const load = async (d) => {
    setLoading(true);
    try {
      const [rs, js] = await Promise.all([
        api.get(`/service-requests?from_date=${d}&to_date=${d}`).catch(() => ({ data: [] })),
        api.get(`/routes/jobs?date=${d}`).catch(() => ({ data: [] })),
      ]);
      // Service requests for the selected date (match preferred or scheduled)
      const rows = (rs.data || []).filter(r =>
        r.scheduled_date === d || r.preferred_date === d);
      setRequests(rows);
      setJobs(js.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(selected); }, [selected]);

  // Light heatmap: count per day of current month
  useEffect(() => {
    const from = ymd(mStart), to = ymd(mEnd);
    api.get(`/service-requests?from_date=${from}&to_date=${to}`)
      .then(r => {
        const rows = r.data || [];
        const counts = {};
        rows.forEach(x => {
          const d = x.scheduled_date || x.preferred_date;
          if (d) counts[d] = (counts[d] || 0) + 1;
        });
        setMonthCounts(counts);
      })
      .catch(() => setMonthCounts({}));
  }, [mStart.getTime()]);  // eslint-disable-line

  // Map pins from requests
  const pins = (requests || [])
    .filter(r => r.latitude != null && r.longitude != null)
    .map(r => ({
      id: r.id,
      name: r.customer_name,
      customer_code: r.customer_code,
      phone: r.customer_phone,
      lat: r.latitude, lng: r.longitude,
    }));

  return (
    <AppShell title="Route · Daily View" subtitle="Week calendar + service requests for the selected day"
      actions={
        <button onClick={() => nav("/operations/routes/new")} data-testid="btn-new-route"
                className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
          <Plus size={14} weight="bold"/>Create Route Plan
        </button>
      }>
      <div className="space-y-4">
        {/* Header: month nav + mode toggle */}
        <div className="bg-white border border-[#E5E2D9] rounded-md p-4" data-testid="daily-header">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <button onClick={() => setCursor(c => addDays(c, mode === "month" ? -30 : -7))}
                      data-testid="cal-prev"
                      className="h-9 w-9 flex items-center justify-center border border-[#E5E2D9] rounded-md hover:bg-[#F7F6F2]">
                <CaretLeft size={12}/>
              </button>
              <div className="text-lg font-medium text-[#2D2B26] min-w-[180px] text-center">
                {cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
              </div>
              <button onClick={() => setCursor(c => addDays(c, mode === "month" ? 30 : 7))}
                      data-testid="cal-next"
                      className="h-9 w-9 flex items-center justify-center border border-[#E5E2D9] rounded-md hover:bg-[#F7F6F2]">
                <CaretRight size={12}/>
              </button>
              <button onClick={() => { const t = new Date(); setCursor(t); setSelected(ymd(t)); }}
                      data-testid="cal-today"
                      className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs hover:bg-[#F7F6F2]">
                Today
              </button>
            </div>
            <div className="flex gap-0">
              <button onClick={() => setMode("week")} data-testid="mode-week"
                      className={`h-9 px-3 text-xs border border-[#E5E2D9] rounded-l-md ${mode === "week" ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white"}`}>Week</button>
              <button onClick={() => setMode("month")} data-testid="mode-month"
                      className={`h-9 px-3 text-xs border border-[#E5E2D9] border-l-0 rounded-r-md ${mode === "month" ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white"}`}>Month</button>
            </div>
          </div>

          {/* DOW header */}
          <div className="grid grid-cols-7 gap-2 text-[10px] uppercase tracking-[0.2em] text-[#8A8780] mb-2">
            {DOW.map(d => <div key={d} className="text-center">{d}</div>)}
          </div>

          {mode === "week" ? (
            <div className="grid grid-cols-7 gap-2" data-testid="week-grid">
              {weekDays.map(d => {
                const s = ymd(d);
                const count = monthCounts[s] || 0;
                const isSel = s === selected;
                const isToday = s === ymd(new Date());
                return (
                  <button key={s} onClick={() => { setSelected(s); setCursor(d); }}
                          data-testid={`day-${s}`}
                          className={`h-20 border rounded-md text-center p-2 transition-colors
                            ${isSel ? "border-[#2D5A27] bg-[#EFF3EE]" : "border-[#E5E2D9] hover:bg-[#FAFAF7]"}
                            ${isToday ? "ring-1 ring-[#3A7033]" : ""}`}>
                    <div className="text-xl font-light">{d.getDate()}</div>
                    {count > 0 && (
                      <div className="text-[10px] text-[#2D5A27] mt-1">
                        {count} pickup{count !== 1 ? "s" : ""}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-2" data-testid="month-grid">
              {monthGrid.map((d, i) => {
                if (!d) return <div key={i} className="h-14"/>;
                const s = ymd(d);
                const count = monthCounts[s] || 0;
                const isSel = s === selected;
                const isToday = s === ymd(new Date());
                return (
                  <button key={s} onClick={() => setSelected(s)}
                          data-testid={`day-${s}`}
                          className={`h-14 border rounded text-center transition-colors relative
                            ${isSel ? "border-[#2D5A27] bg-[#EFF3EE]" : "border-[#F0ECE3] hover:bg-[#FAFAF7]"}
                            ${isToday ? "ring-1 ring-[#3A7033]" : ""}`}>
                    <div className="text-sm">{d.getDate()}</div>
                    {count > 0 && (
                      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#2D5A27]"/>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected day panel */}
        <div className="bg-white border border-[#E5E2D9] rounded-md p-4" data-testid="day-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#2D5A27]">Selected date</div>
              <div className="text-lg font-medium">
                {new Date(selected + "T00:00:00").toLocaleDateString("en-US", {
                  weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </div>
              <div className="text-xs text-[#5C5A55] mt-1">
                {requests.length} service request{requests.length !== 1 ? "s" : ""}
                {jobs.length > 0 && ` · ${jobs.length} route job${jobs.length !== 1 ? "s" : ""}`}
                {loading && <span className="text-[#8A8780]"> · loading…</span>}
              </div>
            </div>
            <div className="flex gap-0">
              <button onClick={() => setView("list")} data-testid="day-view-list"
                      className={`h-9 px-3 text-xs border border-[#E5E2D9] rounded-l-md inline-flex items-center gap-1.5 ${view === "list" ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white"}`}>
                <ListIcon size={12}/>List
              </button>
              <button onClick={() => setView("map")} data-testid="day-view-map"
                      className={`h-9 px-3 text-xs border border-[#E5E2D9] border-l-0 rounded-r-md inline-flex items-center gap-1.5 ${view === "map" ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white"}`}>
                <MapTrifold size={12}/>Map
              </button>
            </div>
          </div>

          {jobs.length > 0 && (
            <div className="mb-3 p-3 border border-[#F0ECE3] rounded bg-[#FAF8F2]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#2D5A27] mb-2">Route jobs on this date</div>
              <div className="space-y-1">
                {jobs.map(j => (
                  <button key={j.id} onClick={() => nav(`/operations/routes/${j.id}`)}
                          data-testid={`job-${j.id}`}
                          className="w-full flex items-center gap-2 p-2 rounded hover:bg-white text-left">
                    <Path size={12} className="text-[#2D5A27]"/>
                    <span className="font-mono text-xs">{j.job_no}</span>
                    <span className="flex-1 text-sm">{j.name}</span>
                    <span className="text-xs text-[#5C5A55]">{j.stop_count} stops · {j.assigned_staff_name || "unassigned"}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {requests.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#8A8780]">
              <Calendar size={28} className="mx-auto mb-2 opacity-40"/>
              No service requests on this date.
            </div>
          ) : view === "map" ? (
            <RouteMap customers={pins} height="480px"/>
          ) : (
            <div className="overflow-x-auto" data-testid="day-request-list">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] text-left">
                  <tr>
                    <th className="py-2 px-2">Customer</th>
                    <th className="py-2 px-2">Zone</th>
                    <th className="py-2 px-2">Status</th>
                    <th className="py-2 px-2">Vehicle / Staff</th>
                    <th className="py-2 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(r => (
                    <tr key={r.id} className="border-t border-[#F0ECE3]">
                      <td className="py-2 px-2">
                        <div className="font-medium">{r.customer_name}</div>
                        <div className="text-xs text-[#5C5A55] font-mono">{r.customer_code}</div>
                      </td>
                      <td className="py-2 px-2 text-xs text-[#5C5A55]">{r.zone_name || "—"}</td>
                      <td className="py-2 px-2">
                        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${STATUS_STYLE[r.status] || "bg-[#E5E2D9]"}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-xs">
                        {r.assigned_vehicle_no && <div>{r.assigned_vehicle_no}</div>}
                        {r.assigned_staff_name && <div className="text-[#5C5A55]">{r.assigned_staff_name}</div>}
                        {!r.assigned_staff_name && <span className="text-[#8A8780]">unassigned</span>}
                      </td>
                      <td className="py-2 px-2 text-right">
                        <button onClick={() => nav(`/operations/service-requests/${r.id}`)}
                                className="text-[#2D5A27] text-xs hover:underline">open</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
